/** Anzeige & CSV für synthetische VTHO-Generierung (kein On-Chain-Tx-Hash). */

export function isSyntheticVthoRow(row = {}) {
  if (row?.syntheticVthoAccrual === true) return true;
  if (row?.syntheticVthoDailyRollup === true) return true;
  const h = String(row?.hash || "").trim();
  return (
    h.startsWith("vtho-generated:") ||
    h.startsWith("vtho-generated-day:") ||
    h.startsWith("vtho-synthetic-")
  );
}

export function isSyntheticVthoDailyRollup(row = {}) {
  if (row?.syntheticVthoDailyRollup === true) return true;
  return String(row?.hash || "").trim().startsWith("vtho-generated-day:");
}

/** Nur YYYY-MM-DD aus vtho-generated-day-Hashes (optional mit :wallet:… Suffix). */
export function parseSyntheticVthoDayKey(hash = "") {
  const h = String(hash || "").trim();
  if (!h.startsWith("vtho-generated-day:")) return "";

  const rest = h.slice("vtho-generated-day:".length);
  const dayMatch = rest.match(/^(\d{4}-\d{2}-\d{2})/);
  if (dayMatch) return dayMatch[1];

  return rest.split(":")[0] || "";
}

function formatDailyRollupWalletLabel(row = {}, language = "de") {
  const custody = String(row?.custodyLocationId || "").trim().toLowerCase();
  const wallet = String(
    row?.wallet ||
      row?.walletAddress ||
      row?.generationWallet ||
      row?.owner ||
      ""
  ).trim().toLowerCase();

  const addr = custody.startsWith("wallet:")
    ? custody.slice("wallet:".length)
    : wallet;

  if (!addr || addr === "unknown" || addr === "0x0000000000000000000000000000000000000000") {
    return "";
  }

  return addr;
}

export function parseSyntheticVthoHash(hash = "") {
  const h = String(hash || "").trim();
  if (!h.startsWith("vtho-generated:")) return null;

  const rest = h.slice("vtho-generated:".length);
  const firstColon = rest.indexOf(":");
  const secondColon = rest.indexOf(":", firstColon + 1);
  if (firstColon < 0 || secondColon < 0) return null;

  const start = Number(rest.slice(0, firstColon));
  const end = Number(rest.slice(firstColon + 1, secondColon));
  const walletPart = rest.slice(secondColon + 1);
  const wallets = walletPart
    ? walletPart.split(",").map((a) => a.trim().toLowerCase()).filter(Boolean)
    : [];

  return {
    start,
    end,
    walletCount: wallets.length,
    wallets,
    raw: h,
  };
}

function toSec(ts) {
  const n = Number(ts || 0);
  if (!n) return 0;
  return n > 1e12 ? Math.floor(n / 1000) : Math.floor(n);
}

function fmtUtcIso(tsSec) {
  const n = Number(tsSec || 0);
  if (!n) return "";
  try {
    return new Date(n * 1000).toISOString().replace(".000Z", "Z");
  } catch {
    return String(n);
  }
}

function intervalMeta(row = {}) {
  const parsed = parseSyntheticVthoHash(row.hash);
  const start = Number(row.generatedFromTs ?? parsed?.start ?? 0);
  const end = Number(
    row.generatedToTs ?? row.timestamp ?? row.ts ?? parsed?.end ?? 0
  );
  const walletCount = parsed?.walletCount ?? 0;
  const durationSec = Number(
    row.intervalDurationSec ?? (start && end ? end - start : 0)
  );
  return { start, end, walletCount, durationSec, parsed };
}

function fmtDuration(seconds, language = "de") {
  const sec = Math.max(0, Math.floor(Number(seconds || 0)));
  if (!sec) return "";
  const en = language === "en";
  if (sec < 60) return en ? `${sec}s` : `${sec} Sek.`;
  if (sec < 3600) {
    const m = Math.round(sec / 60);
    return en ? `${m} min` : `${m} Min.`;
  }
  if (sec < 86400) {
    const h = (sec / 3600).toFixed(1).replace(/\.0$/, "");
    return en ? `${h} h` : `${h} Std.`;
  }
  const d = (sec / 86400).toFixed(1).replace(/\.0$/, "");
  return en ? `${d} d` : `${d} Tage`;
}

const REASON_LABELS = {
  de: {
    gas_fee: "Abschnitt endet vor VTHO-Gas (FIFO)",
    gas_abstraction_fee: "Abschnitt endet vor VTHO-Gas (Abstraktion)",
    exchange_withdrawal_fee: "Abschnitt endet vor Börsen-Auszahlungsgebühr",
    exchange_deposit_fee: "Abschnitt endet vor Börsen-Einzahlungsgebühr",
    swap_out: "Abschnitt endet vor VTHO-Swap/Verkauf",
    nfbc_purchase: "Abschnitt endet vor NFBC-Kauf (VTHO)",
    nft_mint: "Abschnitt endet vor NFT-Mint (VTHO)",
    nft_galaxy_mint: "Abschnitt endet vor Galaxy-Mint (VTHO)",
    mint_payment: "Abschnitt endet vor Mint-Zahlung (VTHO)",
    sent: "Abschnitt endet vor VTHO-Versand",
    vet_balance_change: "Abschnitt endet bei VET-Bestandsänderung",
    legacy_model_end: "Abschnitt bis Modell-Ende (02.12.2025)",
    vtho_outflow: "Abschnitt endet vor VTHO-Abfluss",
    timeline_split: "Zeitabschnitt (technische Aufteilung)",
  },
  en: {
    gas_fee: "Interval ends before VTHO gas (FIFO)",
    gas_abstraction_fee: "Interval ends before VTHO gas (abstraction)",
    exchange_withdrawal_fee: "Interval ends before exchange withdrawal fee",
    exchange_deposit_fee: "Interval ends before exchange deposit fee",
    swap_out: "Interval ends before VTHO swap/sale",
    nfbc_purchase: "Interval ends before NFBC purchase (VTHO)",
    nft_mint: "Interval ends before NFT mint (VTHO)",
    nft_galaxy_mint: "Interval ends before galaxy mint (VTHO)",
    mint_payment: "Interval ends before mint payment (VTHO)",
    sent: "Interval ends before VTHO send",
    vet_balance_change: "Interval ends on VET balance change",
    legacy_model_end: "Interval through model end (2025-12-02)",
    vtho_outflow: "Interval ends before VTHO outflow",
    timeline_split: "Time slice (technical split)",
  },
};

/** Kurzer Grund, warum genau dieses Intervall existiert */
export function formatIntervalSplitReason(row = {}, language = "de") {
  const en = language === "en";
  const lang = en ? "en" : "de";
  const reason = String(
    row.intervalEndReason || row.intervalEndTriggerClass || ""
  ).toLowerCase();
  const labels = REASON_LABELS[lang] || REASON_LABELS.de;
  const label = labels[reason] || labels.vtho_outflow;

  const { durationSec } = intervalMeta(row);
  const dur = fmtDuration(durationSec, language);
  const triggerHash = String(row.intervalEndTriggerHash || "").trim();
  const shortHash =
    triggerHash.length > 14
      ? `${triggerHash.slice(0, 8)}…${triggerHash.slice(-6)}`
      : triggerHash;

  const parts = [label];
  if (dur) parts.push(en ? `duration ${dur}` : `Dauer ${dur}`);
  if (shortHash) {
    parts.push(en ? `trigger tx ${shortHash}` : `Auslöser ${shortHash}`);
  }

  return parts.join(" · ");
}

/** UI: Hauptzeile + optional Intervall / Wallet-Anzahl / Split-Grund */
export function formatSyntheticVthoDisplay(row, language = "de") {
  const en = language === "en";

  if (isSyntheticVthoDailyRollup(row)) {
    const day =
      parseSyntheticVthoDayKey(row?.hash) ||
      (row.timestamp
        ? new Date(toSec(row.timestamp) * 1000).toISOString().slice(0, 10)
        : "");
    const count = Number(row.sourceIntervalCount || 0);
    const label = en ? "Generated (daily total)" : "Generiert (Tageswert)";
    const range = day ? (en ? `UTC day ${day}` : `UTC-Tag: ${day}`) : "";
    const wallets = formatDailyRollupWalletLabel(row, language);
    const splitReason =
      count > 0
        ? en
          ? `${count} interval slice(s) rolled up`
          : `${count} Intervalle zusammengefasst`
        : en
          ? "Daily rollup for display"
          : "Tageszusammenfassung für Anzeige";
    const full = [label, range, splitReason, wallets].filter(Boolean).join(" · ");
    return { label, range, wallets, splitReason, full };
  }

  const label = en ? "Generated (synthetic)" : "Generiert (synthetisch)";
  const { start, end, walletCount } = intervalMeta(row);
  const splitReason = formatIntervalSplitReason(row, language);

  if (!start && !end) {
    return { label, range: "", wallets: "", splitReason, full: label };
  }

  const range =
    start && end
      ? `${fmtUtcIso(start)} → ${fmtUtcIso(end)}`
      : start
        ? fmtUtcIso(start)
        : fmtUtcIso(end);

  const wallets =
    walletCount > 0
      ? en
        ? `${walletCount} wallet(s) in set`
        : `${walletCount} Wallets im Set`
      : "";

  const full = [label, range, splitReason, wallets].filter(Boolean).join(" · ");

  return { label, range, wallets, splitReason, full };
}

/** CSV / kompakte Hash-Spalte — ohne Wallet-Adressliste */
export function formatSyntheticVthoHashForExport(row, language = "de") {
  const en = language === "en";

  if (isSyntheticVthoDailyRollup(row)) {
    const day = parseSyntheticVthoDayKey(row?.hash);
    return day ? `SYN-VTHO-DAY|${day}` : en ? "SYN-VTHO (daily)" : "SYN-VTHO (Tageswert)";
  }

  const { start, end, walletCount } = intervalMeta(row);

  if (!start && !end) {
    return en ? "SYN-VTHO (synthetic)" : "SYN-VTHO (synthetisch)";
  }

  return `SYN-VTHO|${fmtUtcIso(start)}|${fmtUtcIso(end)}|wallets=${walletCount}`;
}

export function getSyntheticVthoCsvFields(row) {
  if (!isSyntheticVthoRow(row)) {
    return {
      eventIdType: "on_chain",
      syntheticIntervalFromUtc: "",
      syntheticIntervalToUtc: "",
      syntheticIntervalEndReason: "",
      syntheticIntervalDurationSec: "",
      walletSetWalletCount: "",
      internalEventId: "",
    };
  }

  const { start, end, durationSec, walletCount } = intervalMeta(row);

  if (isSyntheticVthoDailyRollup(row)) {
    const day = parseSyntheticVthoDayKey(row?.hash);
    return {
      eventIdType: "synthetic_vtho_daily_rollup",
      syntheticIntervalFromUtc: start ? fmtUtcIso(start) : day ? `${day}T00:00:00Z` : "",
      syntheticIntervalToUtc: end ? fmtUtcIso(end) : day ? `${day}T23:59:59Z` : "",
      syntheticIntervalEndReason: "daily_rollup",
      syntheticIntervalDurationSec: durationSec ? String(durationSec) : "",
      walletSetWalletCount: walletCount ? String(walletCount) : "",
      internalEventId: String(row.hash || "").trim(),
      sourceIntervalCount: row.sourceIntervalCount != null ? String(row.sourceIntervalCount) : "",
    };
  }

  return {
    eventIdType: "synthetic_vtho_generated",
    syntheticIntervalFromUtc: start ? fmtUtcIso(start) : "",
    syntheticIntervalToUtc: end ? fmtUtcIso(end) : "",
    syntheticIntervalEndReason: String(row.intervalEndReason || ""),
    syntheticIntervalDurationSec: durationSec ? String(durationSec) : "",
    walletSetWalletCount: walletCount ? String(walletCount) : "",
    internalEventId: String(row.hash || "").trim(),
  };
}

/** Tx-Hash-Spalte: on-chain unverändert, synthetisch kurz */
export function pickDisplayHash(row, language = "de") {
  if (isSyntheticVthoRow(row)) {
    return formatSyntheticVthoHashForExport(row, language);
  }
  return String(row?.hash || row?.txid || row?.event_id || row?.txHash || "").trim();
}

/** CSV: Generated-VTHO-Mengen mit mehr Präzision (vermeidet 0-Menge bei Mini-EUR). */
export function pickExportVthoTokenAmount(row = {}, decimals = 12) {
  const amount = Number(row?.amount ?? row?.qty ?? row?.quantity ?? 0);
  const isGenerated =
    isSyntheticVthoRow(row) ||
    String(row?.classification || row?.type || "").toLowerCase() === "generated";
  if (!isGenerated || !Number.isFinite(amount)) return amount;
  return Number(amount.toFixed(decimals));
}

/** Steuerpflichtiger Betrag bei generiertem VTHO = Marktwert (Einkommen), nicht FIFO-G/V */
export function pickSyntheticVthoIncomeFiat(row = {}) {
  const taxable =
    row.taxableProfitFiat ??
    row.taxableProfitEUR ??
    row.taxableProfit;
  if (taxable != null && Number.isFinite(Number(taxable))) return Number(taxable);

  const total = row.amountFiat ?? row.totalFiat ?? row.euroAmount;
  if (total != null && Number.isFinite(Number(total))) return Number(total);

  return null;
}
